import * as React from 'react'
import * as PropTypes from 'prop-types'
import styled, { css } from 'styled-components'
import { Slide } from 'react-reveal'

import Icon from '../../../../components/ui/Icon/Icon'

import Loader from '../../../../assets/img/loader.gif'
import MoreHeaderParticles from '../../../../assets/img/more-header-particles.svg'

import { MAIN } from '../../../../services/data/profilePages.json'
import dateFormat, {
  dateTimeFormat,
} from '../../../../services/data/dateFormat'
import filterString from '../../../../services/data/filterString'
import * as NotificationsApiServices from '../../../../services/api/requests/Notifications'
import { setGACustomEvent } from '../../../../services/analytics/google-analytics'

const MainPage = styled.div`
  height: 100vh;
  display: ${props => (props.show ? 'flex' : 'none')};
  flex-direction: column;
  overflow: hidden;
`

const Header = styled.div`
  position: relative;
  display: flex;
  flex: 0 0 auto;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  min-height: 87px;
  font-size: 0.7rem;
  background: var(--color-primary);

  &::before {
    content: ' ';
    position: absolute;
    top: 0;
    right: 0;
    left: 0;
    bottom: 0;
    background: url('${MoreHeaderParticles}');
    background-repeat: no-repeat;
    background-position: right top;
  }
`

const ReturnIcon = styled.a`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 2rem;
  height: 2rem;
  border-radius: 2rem;
  cursor: pointer;
  font-size: 0.92rem;
  margin-left: 20px !important;
  margin-right: 20px !important;
  color: var(--color-fg-light);
  background: rgba(255, 255, 255, 0.5);
  z-index: 1;

  & > svg {
    position: relative;
    color: #ffffff;
  }
`

const ToolDropdownIcon = styled.a`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 2rem;
  height: 2rem;
  cursor: pointer;
  font-size: 0.92rem;
  margin-left: 20px !important;
  margin-right: 20px !important;
  color: var(--color-fg-light);
  z-index: 1;

  & > svg {
    position: relative;
    color: #ffffff;
  }
`

const ReadAll = styled.button`
  position: absolute;
  top: 0;
  right: 0;
  color: var(--color-primary);
  font-size: 0.875rem;
  font-weight: var(--font-weight-bold);
  white-space: nowrap;
  border: 0;
  height: 48px;
  padding: 0 1.375rem;
  background: #ffffff;
  box-shadow: var(--box-shadow);
  border-radius: var(--border-radius-form);
  display: ${props => (props.show ? 'block' : 'none')};
`

const Title = styled.h1`
  font-family: var(--font-family-ayala-display-sans);
  flex: 1;
  padding: 20px 0;
  margin: 0;
  margin-right: 60px;
  color: #ffffff;
  font-size: 1.125rem;
  font-weight: var(--font-weight-boldest);
  text-transform: uppercase;
  text-align: center;

  ${props =>
    props.noRightPad &&
    css`
      margin-right: 0;
    `}
`

const Content = styled.div`
  padding: 0;
  overflow: auto;
`

const ItemHeader = styled.div`
  display: flex;
`

const ItemTitle = styled.h4`
  font-size: 1.2rem;
  font-weight: var(--font-weight);
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 250px;
  margin-right: 1rem;
  flex: 1;
`

const ListItem = styled.div`
  overflow: hidden;
  cursor: pointer;
  padding: 2rem;
  padding-left: 0;
  display: flex;
  border-bottom: 1px solid var(--color-fg-lighter);

  &::before {
    content: ' ';
    display: block;
    width: 0.5rem;
    height: 0.5rem;
    border-radius: 1rem;
    margin: 0.75rem 0.5rem 0.75rem 1rem;
    flex: 0 0 auto;
    background: ${props =>
      props.read ? 'transparent' : 'var(--color-primary)'};
  }

  ${ItemTitle} {
    color: var(${props => (props.read ? '--color-fg' : '--color-primary')});
    font-weight: ${props => (props.read ? '300' : '700')};
  }
`

const DetailWrapper = styled.div`
  flex: 1;
  min-width: 0;
`

const ItemDescription = styled.p`
  font-size: 0.8rem;
  margin: 0;
  color: var(--color-fg-light);
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
`

const ItemDate = styled.span`
  color: var(--color-fg-light);
  font-size: 0.8rem;
  white-space: nowrap;
  display: flex;
  align-items: center;
`

const BlankContainer = styled.div`
  padding: 2rem;
  text-align: center;

  ${props =>
    props.small &&
    css`
      padding: 1rem;
      font-size: 0.875rem;
    `}
`

const StyledLoader = styled.img.attrs({ src: Loader })`
  max-height: 30px;
`

const DetailPageContainer = styled.div`
  height: 100vh;
  display: ${props => (props.show ? 'block' : 'none')};
  background-color: inherit;
  transition-property: width;
  transition-duration: var(--animation-duration);
  transition-timing-function: var(--animation-linear);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`

const DetailPageContent = styled.div`
  padding: 2rem;
  overflow: auto;
`

const SubTitle = styled.h5`
  font-family: var(--font-family-ayala-display-sans);
  font-size: 1.25rem;
  line-height: 1.2;
  margin: 0;
  margin-bottom: 0.2rem;
`

const MessageDate = styled.h5`
  font-size: 1rem;
  font-weight: var(--font-weight);
  color: var(--color-fg-light);
  margin: 0;
  margin-bottom: 1rem;
`

const LoadMore = styled.a`
  cursor: pointer;
`

const DetailPageDescription = styled.div`
  > figure {
    margin: 0;
  }

  > * > img {
    max-width: 100%;
  }
`

const MESSAGE_LIMIT = 5

class MessagesPage extends React.Component {
  state = {
    loadMoreStatus: 0,
    loadingStatus: null,
    messages: [],
    selectedMessage: null,
    displayReadAll: false,
  }

  componentDidMount() {
    const { profileDetailPage, setProfileModalDetailPage } = this.props

    if (profileDetailPage) {
      this.showDetailPage({ id: profileDetailPage })
      setProfileModalDetailPage(null)
    }

    this.loadMessages()
  }

  toggleDisplayReadAll = (state, e) => {
    if (e && e.preventDefault) {
      e.preventDefault()
      e.stopPropagation()
    }

    this.setState({
      displayReadAll: state,
    })
  }

  readAllMessages = async () => {
    const { accessToken } = this.props

    try {
      await NotificationsApiServices.postReadAllNotifications(accessToken)
    } catch (err) {}

    await this.setState({
      displayReadAll: false,
      messages: [],
    })

    this.loadMessages()
  }

  showDetailPage = async data => {
    const { accessToken } = this.props
    const { messages } = this.state

    try {
      setGACustomEvent('in_app_notification_interaction', {
        item_id: data.id,
        item_name: data.title,
      })
      const {
        data: messageData,
      } = await NotificationsApiServices.getNotification(data.id, accessToken)
      const updatedMessages = messages.map(message =>
        message.id === messageData.id ? messageData : message,
      )

      this.setState({
        messages: updatedMessages,
        selectedMessage: messageData,
      })
    } catch (err) {
      this.setState({
        selectedMessage: data,
      })
    }
  }

  closeDetailPage = () => {
    this.setState({
      selectedMessage: null,
    })
  }

  loadMessages = async () => {
    const { messages } = this.state
    let lastId = ''
    if (messages.length) {
      lastId = [...messages].pop().id
    }

    this.setState({
      loadingStatus: 0,
      loadMoreStatus: 0,
    })

    const { accessToken } = this.props

    try {
      const messageResponse = await NotificationsApiServices.getAllNotifications(
        accessToken,
        lastId,
        MESSAGE_LIMIT,
      )

      this.setState({
        loadingStatus: 1,
        messages: [...messages, ...messageResponse.data],
        loadMoreStatus: messageResponse.data.length ? 1 : 2,
      })
    } catch (err) {
      this.setState({
        loadingStatus: 4,
        loadMoreStatus: 0,
      })
    }
  }

  render() {
    const {
      loadingStatus,
      messages,
      selectedMessage,
      loadMoreStatus,
      displayReadAll,
    } = this.state
    const { setProfileModalPage } = this.props

    const DetailPage = ({ data }) => {
      const show = !!data
      const { title, inAppBody, publishedAt } = data || {}

      return (
        <Slide right duration={200}>
          <DetailPageContainer show={show}>
            {!!show && (
              <>
                <Header>
                  <ReturnIcon onClick={this.closeDetailPage}>
                    <Icon name="slide-left" />
                  </ReturnIcon>
                  <Title>Inbox</Title>
                </Header>
                <DetailPageContent>
                  <SubTitle>{title}</SubTitle>
                  <MessageDate>{dateTimeFormat(publishedAt)}</MessageDate>
                  <DetailPageDescription
                    dangerouslySetInnerHTML={{ __html: inAppBody }}
                  />
                </DetailPageContent>
              </>
            )}
          </DetailPageContainer>
        </Slide>
      )
    }

    return (
      <>
        <MainPage
          show={!selectedMessage}
          onClick={() => this.toggleDisplayReadAll(false)}
        >
          <Header>
            <ReturnIcon onClick={() => setProfileModalPage(MAIN)}>
              <Icon name="slide-left" />
            </ReturnIcon>
            <Title noRightPad>Inbox</Title>
            <ToolDropdownIcon onClick={e => this.toggleDisplayReadAll(true, e)}>
              <Icon name="v-ellipses" />
              <ReadAll show={displayReadAll} onClick={this.readAllMessages}>
                Mark all as read
              </ReadAll>
            </ToolDropdownIcon>
          </Header>
          <Content>
            {messages.map(props => (
              <ListItem
                key={props.id}
                onClick={() => this.showDetailPage(props)}
                read={props.read}
              >
                <DetailWrapper>
                  <ItemHeader>
                    <ItemTitle>{props.title}</ItemTitle>
                    <ItemDate>
                      {dateFormat(props.publishedAt, null, true)}
                    </ItemDate>
                  </ItemHeader>
                  <ItemDescription>
                    {filterString(props.inAppBody || '')}
                  </ItemDescription>
                </DetailWrapper>
              </ListItem>
            ))}
            {loadingStatus === 0 && (
              <BlankContainer small>
                <StyledLoader />
              </BlankContainer>
            )}
            {!!loadMoreStatus &&
              loadMoreStatus === 1 &&
              messages.length >= MESSAGE_LIMIT && (
                <BlankContainer small>
                  <LoadMore onClick={this.loadMessages}>Load More</LoadMore>
                </BlankContainer>
              )}
            {loadingStatus === 1 && messages.length === 0 && (
              <BlankContainer small>No Records Found</BlankContainer>
            )}
          </Content>
        </MainPage>
        <DetailPage data={selectedMessage} />
      </>
    )
  }
}

MessagesPage.defaultProps = {
  messages: [],
}

MessagesPage.propTypes = {
  messages: PropTypes.array,
}

export default MessagesPage
